/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { FiCheckCircle, FiCircle, FiInfo } from 'react-icons/fi';
import {
  formatDate,
  formatPrice,
  paxTypeLabel,
  startOfToday,
} from '../../utils/packageUtils';
import './PackageFareSummary.scss';

/** "Balance due today" once the payment window has closed, else "Balance by <date>". */
const balanceText = (due) => {
  if (!due) return 'Balance';
  const today = startOfToday();
  return due.getTime() <= today.getTime()
    ? 'Balance due today'
    : `Balance by ${formatDate(due)}`;
};

/** The quoted fare: per room-sharing lines, taxes, total and the payment schedule. */
function PackageFareSummary({
  fare,
  balanceDue,
  travellersDone,
  title,
  paidLabel,
}) {
  const currency = fare?.currency || 'INR';
  const money = (v) => formatPrice(v, currency);
  const breakdown = Array.isArray(fare?.breakdown)
    ? fare.breakdown.filter((b) => b?.roomType)
    : [];
  const total = Number(fare?.grandTotal) || 0;
  const deposit = Number(fare?.depositAmount) || 0;
  const balance = Number(fare?.balanceAmount) || 0;

  return (
    <aside className="pkg-fare">
      <h3 className="pkg-fare__title">{title}</h3>
      <span className="pkg-fare__underline" aria-hidden />

      <ul className="pkg-fare__rows">
        {breakdown.map((b) => (
          <li key={b.roomType} className="pkg-fare__row">
            <span>
              {paxTypeLabel(b.roomType)}
              {b.pricePp != null && b.paxCount != null && (
                <small>
                  {money(b.pricePp)} × {b.paxCount}
                </small>
              )}
            </span>
            <strong>{money(b.amount)}</strong>
          </li>
        ))}
        {Number(fare?.taxTotal) > 0 && (
          <li className="pkg-fare__row">
            <span>Taxes &amp; Fees</span>
            <strong>{money(fare.taxTotal)}</strong>
          </li>
        )}
      </ul>

      <div className="pkg-fare__divider" />

      <div className="pkg-fare__total">
        <span>{paidLabel}</span>
        <strong>{total > 0 ? money(total) : '—'}</strong>
      </div>

      {deposit > 0 && (
        <div className="pkg-fare__schedule">
          <div>
            <span>Deposit</span>
            <strong>{money(deposit)}</strong>
          </div>
          {balance > 0 && (
            <div>
              <span>{balanceText(balanceDue)}</span>
              <strong>{money(balance)}</strong>
            </div>
          )}
        </div>
      )}

      <p className="pkg-fare__note">
        <FiInfo />
        Pay from your agency wallet or online (card, net banking, UPI). The
        supplier confirms hotels and timings with the booking.
      </p>

      {travellersDone !== null && (
        <ul className="pkg-fare__checks">
          <li className={travellersDone ? 'is-done' : ''}>
            {travellersDone ? <FiCheckCircle /> : <FiCircle />}
            Traveller details
          </li>
        </ul>
      )}
    </aside>
  );
}

PackageFareSummary.propTypes = {
  fare: PropTypes.object,
  balanceDue: PropTypes.instanceOf(Date),
  travellersDone: PropTypes.bool,
  title: PropTypes.string,
  paidLabel: PropTypes.string,
};

PackageFareSummary.defaultProps = {
  fare: null,
  balanceDue: null,
  travellersDone: null,
  title: 'Fare Summary',
  paidLabel: 'Total Payable',
};

export default PackageFareSummary;
